import Map from 'ol/Map';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import WKT from 'ol/format/WKT.js';
import { Image as ImageLayer, Layer } from 'ol/layer.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Geometry, LineString, MultiLineString, MultiPolygon, Polygon } from 'ol/geom';
import { Feature, ImageTile, MapBrowserEvent, Tile } from 'ol';
import { Fill, Stroke, Style } from 'ol/style';
import { Extent } from 'ol/extent';
import { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import CircleStyle from 'ol/style/Circle';
import { defaults as defaultControls } from 'ol/control.js';
import OSM from 'ol/source/OSM.js';
import TileLayer from 'ol/layer/Tile';
import 'ol-ext/geom/LineStringSplitAt';
import { Coordinate } from 'ol/coordinate';
import BingMaps from 'ol/source/BingMaps.js';
import { TileWMS } from 'ol/source';
import axios from 'axios';
import { LoadFunction } from 'ol/Tile';
import { transform } from 'ol/proj';

import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { GeopostWMSSource } from 'features/seismic/models/classes/layers/GeopostWMSSource';
import { useMapStore } from 'features/seismic/stores/useMapStore';
import { colorWithAlpha } from 'features/seismic/utils/colorWithAlphaUtils';
import { ILineString } from 'features/seismic/models/interfaces/ILineString';
import { getTrace } from 'features/seismic/utils/calculateScreenValuesUtils';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { useGetSurveyGeometry } from '../api/useWorldMapController';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { splitLineByLine } from 'features/seismic/utils/turfUtils';
import { mouseToMap } from 'features/seismic/utils/seismicToMapCalculate';
import { useSessionStore } from 'session/useSessionStore';
import { MinimapBaseLayer } from 'features/seismic-3d/models/enums/MinimapBaseLayer';
import { SeismicCalculator } from 'features/seismic/models/classes/SeismicCalculator';
import { buildBingMapsLayer, buildOSMMapLayer } from 'features/layer-dashboard/utils/mapUtils';

const format = new WKT();
const zIndexLayerSurvey = 9999991;
const zIndexEntitlement = 9999994;
const zIndexLayerSeismic = 9999992;
const zIndexLayerSeismicOverLine = 9999993;
const zIndexLayerMousePosition = 9999995;

export function useCreateMiniMap(opened: boolean, dataGeometryLine: ILineString | null, allLayers: Layer[], visibleLayers: Layer[], hasGeometryError: boolean, mapSrid: number) {
    const { calculator, volumeToken, scaleX, volumeIdentifier, surveyMetadata, createWellInMiniMap, setCreateWellInMiniMap, removeWellInMiniMap, restrict, lines2D, isInverted } = useSeismicStore(state => ({
        restrict: state.restrict,
        calculator: state.calculator,
        volumeToken: state.surveyMetadata?.VolumeToken,
        volumeIdentifier: state.volumeIdentifier,
        scaleX: state.scale.x,
        scaleY: state.scale.y,
        surveyMetadata: state.surveyMetadata,
        createWellInMiniMap: state.createWellInMiniMap,
        setCreateWellInMiniMap: state.setCreateWellInMiniMap,
        removeWellInMiniMap: state.removeWellInMiniMap,
        lines2D: state.lines2D,
        setLines2D: state.setLines2D,
        isInverted: state.isInverted
    }));

    const lineType = useLine3DNavigationStore(state => state.lineType);

    const { map, pointerMoveObserver } = useMapStore(state => ({
        map: state.map,
        pointerMoveObserver: state.pointerMoveObserver
    }));

    const urlParams = getParamsFromUrl();
    const jwt = useSessionStore(state => state.jwtToken);

    const { data: surveyGeometryWkt, error: errorOnLoadSurveyGeometry, isLoading } = useGetSurveyGeometry(volumeToken!, volumeIdentifier, urlParams.sourceApi === 'pgs' ? 4326 : mapSrid, opened);

    const setIsLineGeometryLoading = useSeismicStore(state => state.setIsReLoadingLineGeoemtry);

    const [miniMap, setMiniMap] = useState<Map | null>(null);
    const mapRef = useRef<HTMLDivElement>(null);

    const layerSeismic = useRef<VectorLayer<VectorSource> | null>(null);
    const [geometrySeismic, setGeometrySeismic] = useState<LineString>();
    const [geometrySeismicMap, setGeometrySeismicMap] = useState<LineString>();
    const layerSeismicOverLine = useRef<VectorLayer<VectorSource> | null>(null);
    const changeResolutionEventKey = useRef<EventsKey | null>(null);
    const pointerMoveEventKey = useRef<EventsKey | null>(null);
    const layerMouserPosition = useRef<VectorLayer<VectorSource> | null>(null);

    const [ surveyGeometry, setSurveyGeometry ] = useState<Geometry|null>(null);

    const [errorMessage, setErrorMessage] = useState<string>();

    const [layerSurvey, setLayerSurvey] = useState<VectorLayer<VectorSource> | null>(null);
    const [layerEntilement, setLayerEntitlement] = useState<VectorLayer<VectorSource> | null>(null);

    const currentPointerCoordinateRef = useRef<Coordinate|null>(null);

    const [firstLoad, setFirstLoad] = useState<boolean>(true);

    const minimapBaseLayer = useSessionStore(state => state.tenantConfiguration?.minimapBaseLayer);

    const lineSubGeometries = useMemo(() => {
        if (geometrySeismic instanceof MultiLineString) {
            return (geometrySeismic as MultiLineString).getLineStrings();
        }
    }, [geometrySeismic]);

    const getLineOverlayFeature = useCallback((calculator: SeismicCalculator, seismicGeom: LineString, minTrace: number, maxTrace: number, xMin: number, xMax: number) => {
        if (!surveyMetadata) {
            return;
        }

        if (!seismicGeom) {
            return;
        }

        let xMinFrac = (xMin - minTrace) / (maxTrace - minTrace);
        let xMaxFrac = (xMax - minTrace) / (maxTrace - minTrace);

        if (xMinFrac < 0 || xMinFrac === Infinity || xMinFrac > 1) {
            xMinFrac = 0;
        }

        if (xMaxFrac > 1 || xMaxFrac === Infinity || xMaxFrac < 0) {
            xMaxFrac = 1;
        }

        if (isInverted) {
            xMinFrac = 1 - xMinFrac;
            xMaxFrac = 1 - xMaxFrac;
        }

        let firstCoordinate : Coordinate;
        let lastCoordinate : Coordinate;

        const transformedGeom = new LineString(seismicGeom.getCoordinates().map(c => transform(c, 'EPSG:' + mapSrid, 'EPSG:4326')));

        firstCoordinate = transformedGeom.getCoordinateAt(xMinFrac);
        lastCoordinate = transformedGeom.getCoordinateAt(xMaxFrac);

        const turfLineString = splitLineByLine(transformedGeom, firstCoordinate, lastCoordinate);
        turfLineString.transform('EPSG:4326', 'EPSG:' + mapSrid);

        const feature = new Feature({ geometry: turfLineString });

        const styleOverLine = new Style({
            stroke: new Stroke({
                color: '#00FE00',
                width: 2,
            })
        });

        feature.setStyle(styleOverLine);

        return feature;
    }, [isInverted, surveyMetadata]);

    useEffect(() => {
        if (!!surveyMetadata?.Entitlement3DLimits.EntitlementGeomWkt && !!miniMap) {
            const entitlementGeom = format.readGeometry(surveyMetadata.Entitlement3DLimits.EntitlementGeomWkt, {
                featureProjection: `EPSG:${mapSrid}`,
                dataProjection: `EPSG:${surveyMetadata.Entitlement3DLimits.EntitlementGeomSrid}`,
            });
            const style = new Style({
                stroke: new Stroke({
                    color: 'rgba(0, 100, 214, 1)',
                    width: 2,
                }),
                fill: new Fill({
                    color: 'rgba(0, 89, 179, 0.15)'
                })
            });

            const source = new VectorSource();
            const layer = new VectorLayer({ source });

            if (entitlementGeom instanceof MultiPolygon) {
                const features = entitlementGeom.getCoordinates().map(coordinates => new Feature({ geometry: new Polygon(coordinates) }));
                features.forEach(f => f.setStyle(style));
                source.addFeatures(features);
            }
            else if (entitlementGeom instanceof MultiLineString) {
                const features = entitlementGeom.getCoordinates().map(coordinates => new Feature({
                    geometry: new LineString(coordinates),
                }));
                features.forEach(f => f.setStyle(style));
                source.addFeatures(features);
            }
            else {
                const feature = new Feature({ geometry: entitlementGeom, style });
                source.addFeature(feature);
                feature.setStyle(style);
            }
            console.log('ENTITLEMENT FEATURE ADDED');
            layer.setZIndex(zIndexEntitlement);

            if (!!layerEntilement && layerEntilement !== layer) {
                miniMap.removeLayer(layerEntilement);
            }

            miniMap.addLayer(layer);
            setLayerEntitlement(layer);
        }
    }, [surveyMetadata, miniMap, surveyGeometry]);

    useEffect(() => {
        /*if (pointerMoveObserver){
            pointerMoveObserver.subscribe()
        }*/
    }, [pointerMoveObserver]);

    useEffect(() => {
        if (errorOnLoadSurveyGeometry) {
            setErrorMessage('Error on load survey geometry');
        }
    }, [errorOnLoadSurveyGeometry]);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        let createMiniMap = miniMap;
        if (!miniMap) {
            const baseLayer = minimapBaseLayer === MinimapBaseLayer.OSM ? buildOSMMapLayer() : buildBingMapsLayer();

            createMiniMap = new Map({
                layers: [
                    baseLayer
                ],
                controls: defaultControls({ attribution: false }),
                pixelRatio: 1,
                target: mapRef.current,
                view: new View({
                    zoom: 7,
                    projection: 'EPSG:' + mapSrid,
                }),
            });
            setMiniMap(createMiniMap);
        }

        for (let i = 0; i < allLayers.length; i++) {
            const layer = allLayers[i];
            const layerExists = createMiniMap?.getAllLayers().find(x => x.get('layerKey') && x.get('layerKey') === layer.get('layerKey'));
            if (!layerExists) {
                createMiniMap?.addLayer(allLayers[i]);
            }
        }
    }, [allLayers]);

    useEffect(() => {
        if (!mapRef.current || !surveyGeometryWkt || !miniMap) {
            return;
        }

        const featureSurvey = format.readFeature(surveyGeometryWkt, {
            featureProjection: `EPSG:${mapSrid}`,
            dataProjection: `EPSG:${urlParams.sourceApi === 'pgs' ? 4326 : mapSrid}`,
        });
        const style = !!surveyMetadata?.Entitlement3DLimits.EntitlementGeomWkt ?
            new Style({ stroke: new Stroke({ color:'#FF0000', width: 2}) })
            :
            new Style({
                stroke: new Stroke({
                    color: 'rgba(0, 100, 214, 1)',
                    width: 2,
                }),
                fill: new Fill({
                    color: 'rgba(0, 89, 179, 0.3)'
                })
            });
        featureSurvey.setStyle(style);

        const layer = new VectorLayer({ source: new VectorSource({ features: lineType !== LineType.Line2D ? [featureSurvey] : [] }) });
        layer.setZIndex(zIndexLayerSurvey);

        if (layer !== layerSurvey && layerSurvey !== null) {
            miniMap.removeLayer(layerSurvey);
        }

        setLayerSurvey(layer);

        const geometry = featureSurvey.getGeometry()?.getExtent() as Extent;
        setSurveyGeometry(featureSurvey.getGeometry() ?? null);

        miniMap.addLayer(layer);

        miniMap.getView().fit(geometry);

        let zoom = miniMap.getView().getZoom();
        if (zoom) {
            miniMap.getView().setZoom(zoom - 0.5);
        }

        setFirstLoad(false);

    }, [surveyGeometryWkt, miniMap]);

    // Cria as linhas 2D no mapa quando o viewer for 2D
    useEffect(() => {
        if (surveyMetadata?.Type === SurveyType.Seismic2D && lines2D && miniMap) {
            let featureLines = [];
            console.log('LINES 2D LENGTH ====>', lines2D.length);
            for (let i = 0; i < lines2D.length; i++) {
                if (!!lines2D[i].Wkt) {
                    const featureLine = format.readFeature(lines2D[i].Wkt, {
                        featureProjection: `EPSG:${mapSrid}`,
                        dataProjection: `EPSG:${mapSrid}`,
                    });

                    featureLine.setStyle(new Style({
                        stroke: new Stroke({
                            color: '#FFFF00',
                            width: 1,
                        })
                    }));

                    featureLines.push(featureLine);
                }
            }
            const layerLine = new VectorLayer({ source: new VectorSource({ features: featureLines }) });
            layerLine.setZIndex(zIndexLayerSeismic);
            miniMap.addLayer(layerLine);
        }
    }, [lines2D, miniMap, surveyMetadata]);

    // cria a linha sismica
    useEffect(() => {
        if (!dataGeometryLine && !surveyGeometryWkt) {
            return;
        }
        setIsLineGeometryLoading(false);

        if (layerSeismic.current) {
            miniMap?.removeLayer(layerSeismic.current);
        }
        const wkt = lineType === LineType.Line2D ? surveyGeometryWkt ?? dataGeometryLine?.Line : dataGeometryLine?.Line;
        //console.log('cria a linha sismica, dataGeometryLine', wkt);
        if (!wkt) {
            return;
        }
        else if (!!miniMap) {
            if (lineType === LineType.ZSlice) {
                return;
            }

            if ((!wkt || wkt.toLowerCase() === 'empty')) {
                setErrorMessage('An error occured while loading the line geometry.');
                setGeometrySeismic(undefined);
                setGeometrySeismicMap(undefined);
                return;
            }

            try {
                const featureLineOriginal = format.readFeature(wkt, {
                    featureProjection: `EPSG:${mapSrid}`,
                    dataProjection: `EPSG:${urlParams.sourceApi === 'pgs' ? 4326 : mapSrid}`,
                });

                const featureLine = format.readFeature(wkt, {
                    featureProjection: `EPSG:${mapSrid}`,
                    dataProjection: `EPSG:${urlParams.sourceApi === 'pgs' ? 4326 : mapSrid}`,
                });

                setGeometrySeismic(featureLineOriginal.getGeometry() as LineString);
                setGeometrySeismicMap(featureLine.getGeometry() as LineString);
                let featureGeom = featureLineOriginal.getGeometry();
                setGeometrySeismic(featureGeom as LineString);
                setGeometrySeismicMap(featureLine.getGeometry() as LineString);
                featureLine.setStyle(new Style({
                    stroke: new Stroke({
                        color: (lineType === LineType.Line2D && !!surveyMetadata?.Entitlement3DLimits.EntitlementGeomWkt) ? '#FF0000' : 'rgba(0, 100, 214, 1)',
                        width: 2,
                    })
                }));

                const layerLine = new VectorLayer({ source: new VectorSource({ features: [featureLine] }) });
                layerLine.setZIndex(zIndexLayerSeismic);
                miniMap.addLayer(layerLine);

                layerSeismic.current = layerLine;
            }
            catch {
                setErrorMessage('An error occured when processing the geometry of the line.');
            }
        }
    }, [dataGeometryLine, calculator, miniMap, surveyGeometryWkt]);

    useEffect(() => {
        if (lineType === LineType.ZSlice && !!miniMap) {
            if (!!layerSeismic.current) {
                miniMap.removeLayer(layerSeismic.current);
            }
            if (!!layerSeismicOverLine.current) {
                miniMap.removeLayer(layerSeismicOverLine.current);
            }
            if (!!layerMouserPosition.current) {
                miniMap.removeLayer(layerMouserPosition.current);
            }
        }
    }, [lineType]);

    useEffect(() => {
        if (hasGeometryError) {
            setErrorMessage('An error occured while loading the line geometry.');
        }
    }, [hasGeometryError]);

    // mostra o espaço da tela visivel na linha
    useEffect(() => {
        if (map && scaleX && (!!dataGeometryLine || (lineType === LineType.Line2D && !!surveyGeometryWkt)) && geometrySeismic && geometrySeismicMap && miniMap && restrict) {
            if (!restrict.SeismicDisplayRealTimePositionOnMap) {
                return;
            }

            if (changeResolutionEventKey.current) {
                unByKey(changeResolutionEventKey.current);
            }

            if (lineType === LineType.ZSlice) {
                return;
            }

            changeResolutionEventKey.current = map.on('moveend', function () {
                if (!calculator || !surveyMetadata) {
                    return;
                }

                if (layerSeismicOverLine.current) {
                    miniMap.removeLayer(layerSeismicOverLine.current);
                }

                const size = map.getSize();

                const extent = map.getView().calculateExtent(size);
                const increment = calculator.getLineIncrement(surveyMetadata);
                const start = calculator.getMinTrace(surveyMetadata);

                const xMin = getTrace(extent[0], scaleX, start, increment);
                const xMax = getTrace(extent[2], scaleX, start, increment);

                const maxTrace = lineType !== LineType.Line2D ? dataGeometryLine!.Max : calculator.getMaxTrace(surveyMetadata);
                const minTrace = lineType !== LineType.Line2D ? dataGeometryLine!.Min : calculator.getMinTrace(surveyMetadata);

                const features: Feature[] = [];

                if (!!lineSubGeometries) {
                    surveyMetadata.VolumeDebugInfo?.forEach((limit, i) => {
                        if (xMax > limit.StartTrace && xMin < limit.EndTrace) {
                            const geom = lineSubGeometries[i];
                            let relativeMinX = xMin;
                            if (relativeMinX < limit.StartTrace) {
                                relativeMinX = limit.StartTrace;
                            }

                            let relativeMaxX = xMax;
                            const itemTotalTraces = limit.EndTrace - limit.StartTrace;
                            if (relativeMaxX > limit.EndTrace) {
                                relativeMaxX = limit.EndTrace;
                            }

                            const feature = getLineOverlayFeature(calculator, geom, limit.StartTrace, limit.EndTrace, relativeMinX, relativeMaxX);

                            if (!!feature) {
                                features.push(feature);
                            }
                        }
                    });
                }
                else {
                    const feature = getLineOverlayFeature(calculator, geometrySeismic, minTrace, maxTrace, xMin, xMax);
                    if (!!feature) {
                        features.push(feature);
                    }
                }

                const layerOverLine = new VectorLayer({ source: new VectorSource({ features: features }) });

                layerOverLine.setZIndex(zIndexLayerSeismicOverLine);

                miniMap?.addLayer(layerOverLine);

                layerSeismicOverLine.current = layerOverLine;
            });

            map.dispatchEvent('moveend');
        }
    }, [map, miniMap, scaleX, dataGeometryLine, geometrySeismic, restrict, geometrySeismicMap, calculator, surveyMetadata, isInverted, lineType]);

    // mostra a movimentação do mouse
    useEffect(() => {
        if (pointerMoveEventKey.current) {
            unByKey(pointerMoveEventKey.current);
        }
        if (map && scaleX && miniMap && (dataGeometryLine || (lineType === LineType.Line2D && !!surveyGeometryWkt)) && restrict) {
            if (!restrict.SeismicDisplayRealTimePositionOnMap) {
                return;
            }
            if (lineType === LineType.ZSlice) {
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const pointerMoveCallback = (mapCoordinate: Coordinate) => {
                currentPointerCoordinateRef.current = mapCoordinate;
                const coordinate = mouseToMap(calculator, surveyMetadata, mapCoordinate, dataGeometryLine, isInverted, scaleX, lineSubGeometries ?? [geometrySeismic], surveyMetadata?.VolumeDebugInfo);

                if (!coordinate) {
                    return;
                }

                miniMap?.removeLayer(layerMouserPosition.current!);

                const circleFeature = format.readFeature('POINT(' + coordinate[0] + ' ' + coordinate[1] + ')', {
                    featureProjection: `EPSG:${mapSrid}`,
                    dataProjection: `EPSG:${urlParams.sourceApi === 'pgs' ? 4326 : mapSrid}`,
                });

                circleFeature.setStyle(new Style({
                    image: new CircleStyle({
                        radius: 4,
                        fill: new Fill({
                            color: '#ff0000',
                        }),
                        stroke: new Stroke({
                            color: '#000000',
                        }),
                    })
                }),
                );

                const layerCircle = new VectorLayer({ source: new VectorSource({ features: [circleFeature] }) });
                layerCircle.setZIndex(zIndexLayerMousePosition);
                layerMouserPosition.current = layerCircle;
                miniMap?.addLayer(layerCircle);
            };

            pointerMoveEventKey.current = map.on('pointermove', evt => pointerMoveCallback(evt.coordinate));

            if (!!currentPointerCoordinateRef.current) {
                pointerMoveCallback(currentPointerCoordinateRef.current);
            }
        }
    }, [map, scaleX, geometrySeismic, miniMap, dataGeometryLine, restrict, isInverted, lineType, calculator, surveyGeometryWkt]);

    useEffect(() => {
        if (miniMap) {
            if (layerSeismicOverLine.current) {
                miniMap.removeLayer(layerSeismicOverLine.current);
            }
            if (layerMouserPosition.current) {
                miniMap.removeLayer(layerMouserPosition.current);
            }
        }
    }, [dataGeometryLine]);

    useEffect(() => {
        if (createWellInMiniMap.length > 0 && miniMap) {
            for (let i in createWellInMiniMap) {
                miniMap.addLayer(createWellInMiniMap[i]);
            }
            setCreateWellInMiniMap([]);
        }
    }, [createWellInMiniMap, miniMap]);

    useEffect(() => {
        if (removeWellInMiniMap.length > 0 && miniMap) {
            for (let i in removeWellInMiniMap) {
                miniMap.removeLayer(removeWellInMiniMap[i]);;
            }
        }
    }, [removeWellInMiniMap, miniMap]);

    useEffect(() => {
        allLayers.forEach(layer => visibleLayers.includes(layer) ? layer.setVisible(true) : layer.setVisible(false));
    }, [visibleLayers, allLayers, miniMap]);

    return { mapRef: mapRef, isLoading: isLoading, errorMessage };
}